import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import ElectricMeterIcon from "@mui/icons-material/ElectricMeter";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import BuildIcon from "@mui/icons-material/Build";
import WindowIcon from "@mui/icons-material/Window";
import AssignmentIcon from "@mui/icons-material/Assignment";
import UserIcon from "@mui/icons-material/Person";
import FlagIcon from "@mui/icons-material/Flag";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import StraightenIcon from "@mui/icons-material/Straighten";
import { Switch } from "@mui/material";
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';


const drawerWidth = 280;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  boxShadow: open ? theme.shadows[6] : theme.shadows[2],
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      boxShadow: theme.shadows[6],
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      boxShadow: theme.shadows[2],
    },
  }),
}));

const CustomTooltip = styled(({ className, open, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, open }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    boxShadow: theme.shadows[1],
    fontSize: "1rem",
    visibility: open ? "visible" : "hidden",
  },
}));

export default function Sidebar({ toggleTheme, themeMode }) {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const [itemSelected, setItemSelected] = React.useState("");

  const username = user ? user.firstname : "Account";
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleClick = (item) => {
    setItemSelected(item.text);
    navigate(`/${item.path}`);
  };

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    { text: "Inicio", path: "dashboard", icon: <HomeIcon /> },
    { text: "Ordenes de trabajo", path: "work-orders", icon: <WorkIcon /> },
    { text: "Máquinas", path: "machines", icon: <ElectricMeterIcon /> },
    { text: "Clientes", path: "clients", icon: <PeopleIcon /> },
    {
      text: "Marcas de Máquinas",
      path: "machine-brand",
      icon: <BusinessIcon />,
    },
    { text: "Tipos de Máquinas", path: "machine-types", icon: <BuildIcon /> },
    {
      text: "Caracteristicas de Máquinas",
      path: "machine-type-characteristics",
      icon: <WindowIcon />,
    },

    {
      text: "Tareas de Máquinas",
      path: "machine-type-task-classes",
      icon: <TaskAltIcon />,
    },
    {
      text: "Caracteristicas",
      path: "machine-characteristics",
      icon: <StraightenIcon />,
    },
    { text: "Tipos de tareas", path: "task-classes", icon: <AssignmentIcon /> },
    { text: "Usuarios", path: "users", icon: <UserIcon /> },
    { text: "Estados", path: "statuses", icon: <FlagIcon /> },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Fabrifor
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={`Tema ${themeMode === "dark" ? "claro" : "oscuro"}`}>
            {themeMode === "dark" ? <LightModeIcon sx={{position: "relative", top: "10px" }} /> : <DarkModeIcon sx={{position: "relative", top: "10px" }} /> } 
            <Switch sx={{marginX: "5px", position: "relative", top: "+4px" }} checked={themeMode === "dark"} onChange={toggleTheme} />
          </Tooltip>
          <Divider orientation="vertical" flexItem variant="middle" />
          <Typography variant="body1" sx={{ mr: 1 }}>
            <UserIcon
              style={{
                marginRight: "5px",
                marginLeft: "5px",
                position: "relative",
                top: "+4px",
              }}
            />{" "}
            {username}
          </Typography>
          <Divider orientation="vertical" flexItem variant="middle"/>
          <Tooltip title={"Desconectar"}>
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <List sx={{ flexGrow: 1 }}>
            {menuItems.map((item) => (
              <ListItem
                key={item.text}
                disablePadding
                sx={{ display: "block" }}
              >
                <CustomTooltip title={item.text} placement="right" open={!open}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                    }}
                    onClick={() => handleClick(item)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1.3 : "auto",
                        justifyContent: "center",
                        color:
                          itemSelected === item.text
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText sx={{ opacity: open ? 1 : 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mr: 1,
                          color:
                            itemSelected === item.text
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </ListItemText>
                  </ListItemButton>
                </CustomTooltip>
              </ListItem>
            ))}
          </List>
          <Divider />
          <Box
            sx={{
              height: 64, // Ajusta la altura según sea necesario
              display: open ? "flex" : "none",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette,
            }}
          >
            <Typography variant="caption" color="textSecondary">
              © 2024 IL
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
